import { Router, watchBusy } from "@frui.ts/screens/dist";
import { attachAutomaticValidator, getValidationMessage } from "@frui.ts/validation";
import type { interfaces } from "inversify";
import { computed } from "mobx";
import EventBus from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService from "services/notificationService";
import OptionsService from "services/optionsService";
import SecurityService from "services/securityService";
import { Modules } from "config";

import { UserType } from "../../../entities/userType";
import EnforcementUserRepository from "../../../repositories/enforcementUserRepository";
import EditableDetailViewModel from "../../editableDetailViewModel";
import type EnforcementTabletUserDetailResponse from "entities/enforcementTabletUserDetailResponse";
import { EnforcementUserUpdateValidationRules } from "entities/enforcementTabletUserUpdateRequest";

@Router.registerRoute({ route: ":enforcementUserId", name: Router.Self })
export default class EnforcementUserDetailViewModel extends EditableDetailViewModel<EnforcementTabletUserDetailResponse> {
  readonly modelName = "enforcementUser";

  constructor(
    protected itemId: string,
    private repository: EnforcementUserRepository,
    protected localizationService: LocalizationService,
    protected notificationService: NotificationService,
    protected eventBus: EventBus,
    protected securityService: SecurityService,
    private optionsService: OptionsService
  ) {
    super(itemId);
  }

  @watchBusy
  protected async loadDetail() {
    const item = await this.repository.get(this.itemId);
    attachAutomaticValidator(item, EnforcementUserUpdateValidationRules);

    return item;
  }

  protected updateData() {
    return this.repository.update(this.item);
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.enforcement, `enforcementUsers.${code}`, size);
  }

  @computed
  get enforcementUserTypeOptions() {
    return this.optionsService.internalEnumOptions(UserType);
  }

  @computed
  get isManager() {
    return this.item?.isManager;
  }

  set isManager(isManager) {
    this.item.isManager = !this.item.isManager;
  }

  @computed
  get isManagerError() {
    return getValidationMessage(this.item, "isManager");
  }

  static Factory({ container }: interfaces.Context) {
    return (itemId: string) => {
      return new EnforcementUserDetailViewModel(
        itemId,
        container.get(EnforcementUserRepository),
        container.get(LocalizationService),
        container.get(NotificationService),
        container.get(EventBus),
        container.get(SecurityService),
        container.get(OptionsService)
      );
    };
  }
}
